import { create } from 'zustand';

export const normalizeCusip = (cusip) => {
  if (cusip == null) return '';
  return String(cusip).trim().toUpperCase();
};

const isCusipSelectedOnAnotherRung = (selectedOrders, rungIndex, cusip) => {
  const normalizedCusip = normalizeCusip(cusip);

  return normalizedCusip !== '' && Object.entries(selectedOrders).some(
    ([selectedRung, order]) =>
      String(selectedRung) !== String(rungIndex) &&
      normalizeCusip(order?.cusip) === normalizedCusip
  );
};

const removeDuplicateCusips = (orders) => {
  const seenCusips = new Set();

  return Object.entries(orders || {}).reduce((uniqueOrders, [rungIndex, order]) => {
    const cusip = normalizeCusip(order?.cusip);
    if (cusip && seenCusips.has(cusip)) return uniqueOrders;

    if (cusip) seenCusips.add(cusip);
    uniqueOrders[rungIndex] = order;
    return uniqueOrders;
  }, {});
};

// Helper function to get unique ID
const getRowUniqueId = (orderData) => {
  if (!orderData) return '';
  return `${orderData.cusip || ''}-${orderData.price || ''}-${orderData.qty || ''}-${orderData.rowIndex}`;
};

export const useSelectedLadderOrdersStore = create((set, get) => ({
  // Map of rung index to selected order data
  selectedOrders: {},
  // Store original selected orders (used when coming from edit staging blotter)
  originalSelectedOrders: {},

  // Only set originalSelectedOrders when coming from edit (staging util)
  setOriginalSelectedOrders: (ordersObj) => set(() => {
    const uniqueOrders = removeDuplicateCusips(ordersObj);
    return {
      selectedOrders: uniqueOrders,
      originalSelectedOrders: uniqueOrders,
    };
  }),
  //original will be empty when not coming from edit meaning orderids will go 0
  // Select an order for a specific rung - only updates selectedOrders
  selectOrder: (rungIndex, orderData) => set((state) => {
    if (isCusipSelectedOnAnotherRung(state.selectedOrders, rungIndex, orderData?.cusip)) {
      return state;
    }

    // Create a new orders object to avoid mutating state
    const newOrders = { ...state.selectedOrders };

    // Compute editability: false if DONE or RFQ
    const isEditable = !((orderData?.status === 'DONE') || (orderData?.protocol === 'RFQ'));
    
    // Add the new order for this rung, replacing any existing one
    newOrders[rungIndex] = { 
      ...orderData,
      bondInventoryId: orderData.bondInventoryId || null,
      isEditable
    };
    
    return { selectedOrders: newOrders };
    // Do NOT touch originalSelectedOrders here
  }),
  
  // Check if an order is currently selected for a rung
  isSelected: (rung, bondIdOrData) => {
    const selectedOrders = get().selectedOrders;
    const bondId = typeof bondIdOrData === 'object' ? 
      bondIdOrData.bondInventoryId : 
      bondIdOrData;
      
    if (!bondId || !selectedOrders[rung]) return false;
    
    return selectedOrders[rung].bondInventoryId === bondId;
  },

  // Toggle selection (select if not selected, deselect if already selected)
  toggleOrder: (rung, orderData) => {
    if (!orderData?.bondInventoryId) return;
    set((state) => {
      const currentOrder = state.selectedOrders[rung];
      
      // If current order exists and is not editable, prevent toggling
      if (currentOrder && currentOrder.isEditable === false) {
        return state; // Return unchanged state
      }

      // A CUSIP may only be selected on one ladder rung.
      if (isCusipSelectedOnAnotherRung(state.selectedOrders, rung, orderData.cusip)) {
        return state;
      }
      
      const newState = { ...state };
      if (state.selectedOrders[rung]?.bondInventoryId === orderData.bondInventoryId) {
        delete newState.selectedOrders[rung];
      } else {
        const isEditable = !((orderData?.status === 'DONE') || (orderData?.protocol === 'RFQ'));
        newState.selectedOrders[rung] = {
          ...orderData,
          isEditable
        };
      }
      // Do NOT touch originalSelectedOrders here
      return { selectedOrders: newState.selectedOrders };
    });
  },

  // Remove order for a specific rung
  removeOrder: (rungIndex) => set((state) => {
    const currentOrder = state.selectedOrders[rungIndex];
    
    // If order exists and is not editable, prevent removal
    if (currentOrder && currentOrder.isEditable === false) {
      return state; // Return unchanged state
    }
    
    const newOrders = { ...state.selectedOrders };
    delete newOrders[rungIndex];
    return { selectedOrders: newOrders };
    // Do NOT touch originalSelectedOrders here
  }),

  // Clear all selected orders and originalSelectedOrders
  clearAllOrders: () => set({ selectedOrders: {}, originalSelectedOrders: {} }),
}));

